import type {
  ChangedFieldPathToken,
  ChangeTrackingSource,
} from '@typeDefinitions/changeTrackingTypes';
import type { ProspectData } from '@store/prospect';

import { CHANGE_TRACKING_SOURCES } from '../../utils/changeTrackingRegistry';
import { createFormChangeTracker } from '../../utils/createFormChangeTracker';
import { findChangedForms } from '../../utils/findChangedForms';

const IGNORED_FIELDS = new Set([
  'callLog',
  'createdDate',
  'customerMatchFound',
  'equipmentOptions',
  'lastContactDate',
  'notes',
  'operationType',
  'quotes',
]);

const PROSPECT_INFORMATION_FIELDS = new Set([
  'color',
  'createdBy',
  'createdById',
  'name',
  'public',
  'status',
]);

const ADDRESS_FIELDS = new Set([
  'address1',
  'address2',
  'city',
  'state',
  'zip',
]);

const FOLLOW_UP_FIELDS = new Set(['followUpDate']);

const isEmptyFormValue = (value: unknown): boolean =>
  value === null ||
  value === undefined ||
  value === '' ||
  (Array.isArray(value) && value.length === 0);

const getPropertyPath = (path: readonly ChangedFieldPathToken[]): string[] =>
  path.flatMap((token) => (token.type === 'property' ? [token.key] : []));

const areDatesEqual = (before: string, after: string): boolean => {
  const beforeDate = new Date(before);
  const afterDate = new Date(after);

  return (
    !Number.isNaN(beforeDate.getTime()) &&
    !Number.isNaN(afterDate.getTime()) &&
    beforeDate.getTime() === afterDate.getTime()
  );
};

const areProspectDetailsScalarValuesEqual = (
  before: unknown,
  after: unknown,
  path: readonly ChangedFieldPathToken[],
): boolean => {
  if (Object.is(before, after)) return true;
  if (isEmptyFormValue(before) && isEmptyFormValue(after)) return true;

  if (typeof before === 'number' && typeof after === 'string') {
    return after.trim() !== '' && Number(after) === before;
  }

  if (typeof before === 'string' && typeof after === 'number') {
    return before.trim() !== '' && Number(before) === after;
  }

  if (typeof before === 'string' && typeof after === 'string') {
    const field = getPropertyPath(path).at(-1) ?? '';

    if (/date/i.test(field) && areDatesEqual(before, after)) {
      return true;
    }

    if (/phone|fax/i.test(field)) {
      return before.replace(/\D/g, '') === after.replace(/\D/g, '');
    }
  }

  return false;
};

const getProspectDetailsForm = (
  path: readonly ChangedFieldPathToken[],
): ChangeTrackingSource => {
  const [field] = getPropertyPath(path);

  if (ADDRESS_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.prospectDetails.address;
  }

  if (field === 'contacts') {
    return CHANGE_TRACKING_SOURCES.prospectDetails.contact;
  }

  if (FOLLOW_UP_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.prospectDetails.followUp;
  }

  if (PROSPECT_INFORMATION_FIELDS.has(field)) {
    return CHANGE_TRACKING_SOURCES.prospectDetails.prospectInformation;
  }

  return CHANGE_TRACKING_SOURCES.prospectDetails.prospectInformation;
};

const findProspectDetailsChangedForms = (
  before: ProspectData,
  after: ProspectData,
): ChangeTrackingSource[] =>
  findChangedForms(before, after, {
    ignoredFields: IGNORED_FIELDS,
    areScalarValuesEqual: areProspectDetailsScalarValuesEqual,
    getForm: (change) => getProspectDetailsForm(change.path),
  });

const prospectDetailsFormChangeTracker = createFormChangeTracker<
  ProspectData,
  ChangeTrackingSource
>({
  getIdentity: (prospect) => prospect.id || undefined,
  findChangedForms: findProspectDetailsChangedForms,
});

export const setProspectDetailsChangeBaseline =
  prospectDetailsFormChangeTracker.setBaseline;
export const clearProspectDetailsChangeBaseline =
  prospectDetailsFormChangeTracker.clearBaseline;
export const prepareProspectDetailsChangeSummary =
  prospectDetailsFormChangeTracker.prepare;
